import * as Tone from "tone";

const API_BASE = "/api/ear-training";
const MODULES = [
  { id: "section-1", title: "Core Intervals", description: "Perfect Fourth, Perfect Fifth, octaves, and major thirds across four listening games.", href: "/ear-training/section-1/intro.htm" },
  { id: "section-2", title: "Color and Tension", description: "Adds major sixths, major seconds, minor seconds, and major sevenths across the same games.", href: "/ear-training/section-2/intro.htm" },
  { id: "section-3", title: "Advanced Distance", description: "Adds minor sevenths, flat fifths, tritones, and movable roots.", href: "/ear-training/section-3/intro.htm" },
  { id: "section-4", title: "Triads and Chords", description: "Builds from major and minor triads into sevenths, suspensions, inversions, slash chords, and extensions.", href: "/ear-training/section-4/intro.htm" },
];
const INTERVALS = [
  { id: "m2", label: "Minor Second", semitones: 1, key: "q" },
  { id: "M2", label: "Major Second", semitones: 2, key: "2" },
  { id: "m3", label: "Minor Third", semitones: 3, key: "w" },
  { id: "M3", label: "Major Third", semitones: 4, key: "3" },
  { id: "P4", label: "Perfect Fourth", semitones: 5, key: "4" },
  { id: "b5", label: "Minor Fifth", semitones: 6, key: "r" },
  { id: "TT", label: "Tritone", semitones: 6, key: "r" },
  { id: "P5", label: "Perfect Fifth", semitones: 7, key: "5" },
  { id: "m6", label: "Minor Sixth", semitones: 8, key: "t" },
  { id: "M6", label: "Major Sixth", semitones: 9, key: "6" },
  { id: "m7", label: "Minor Seventh", semitones: 10, key: "y" },
  { id: "M7", label: "Major Seventh", semitones: 11, key: "7" },
  { id: "P8", label: "Octave", semitones: 12, key: "8" },
];
const INTERVAL_BY_ID = new Map(INTERVALS.map((interval) => [interval.id, interval]));
const CHORDS = [
  { id: "majorTriad", label: "Major Triad", shortLabel: "Major", intervals: [0, 4, 7], key: "1" },
  { id: "minorTriad", label: "Minor Triad", shortLabel: "Minor", intervals: [0, 3, 7], key: "2" },
  { id: "minorSeventh", label: "Minor Seventh Chord", shortLabel: "Minor 7", intervals: [0, 3, 7, 10], key: "3" },
  { id: "dominantSeventh", label: "Dominant Seventh Chord", shortLabel: "Dominant", intervals: [0, 4, 7, 10], key: "4" },
  { id: "suspendedFourth", label: "Suspended Fourth Chord", shortLabel: "Sus4", intervals: [0, 5, 7], key: "5" },
  { id: "suspendedSecond", label: "Suspended Second Chord", shortLabel: "Sus2", intervals: [0, 2, 7], key: "6" },
  { id: "firstInversion", label: "First Inversion Triad", shortLabel: "1st Inv.", intervals: [4, 7, 12], key: "7" },
  { id: "secondInversion", label: "Second Inversion Triad", shortLabel: "2nd Inv.", intervals: [7, 12, 16], key: "8" },
  { id: "slashChord", label: "Slash Chord", shortLabel: "Slash", intervals: [-5, 0, 4, 7], key: "q" },
  { id: "diminishedTriad", label: "Diminished Triad", shortLabel: "Dim", intervals: [0, 3, 6], key: "w" },
  { id: "augmentedTriad", label: "Augmented Triad", shortLabel: "Aug", intervals: [0, 4, 8], key: "e" },
  { id: "ninthChord", label: "Ninth Chord", shortLabel: "9th", intervals: [0, 4, 7, 10, 14], key: "r" },
  { id: "eleventhChord", label: "Eleventh Chord", shortLabel: "11th", intervals: [0, 4, 7, 10, 14, 17], key: "t" },
  { id: "thirteenthChord", label: "Thirteenth Chord", shortLabel: "13th", intervals: [0, 4, 7, 10, 14, 21], key: "y" },
];
const CHORD_BY_ID = new Map(CHORDS.map((chord) => [chord.id, chord]));
const SECTION_INTERVALS = {
  "section-1": ["P4", "P5", "P8", "M3"],
  "section-2": ["P4", "P5", "P8", "M3", "M6", "M2", "m2", "M7"],
  "section-3": ["P4", "P5", "P8", "M3", "M6", "M2", "m2", "M7", "m7", "b5", "TT"],
};
const LESSON_BLUEPRINTS = [
  { id: "lesson-1", title: "Lesson 1", mode: "single", hint: "Perfect Fourth/Fifth single", count: 2, adds: "Perfect Fourth and Perfect Fifth single intervals", why: "Starts with the two most stable open intervals as melodic single intervals." },
  { id: "lesson-2", title: "Lesson 2", mode: "harmonic", hint: "Perfect Fourth/Fifth harmonic", count: 2, adds: "Perfect Fourth and Perfect Fifth harmonic intervals", why: "Uses the same intervals, but both notes sound together." },
  { id: "lesson-3", title: "Lesson 3", mode: "single", hint: "add octave", count: 3, adds: "Octaves in single intervals", why: "Adds the same-note-up-high sound before widening the game types." },
  { id: "lesson-4", title: "Lesson 4", mode: "harmonic", hint: "octave harmonic", count: 3, adds: "Octaves in harmonic intervals", why: "Trains the same Perfect Fourth, Perfect Fifth, and octave set as simultaneous sounds." },
  { id: "lesson-5", title: "Lesson 5", mode: "single", hint: "add Major Third", count: 4, adds: "Major Third in single intervals", why: "Adds the bright major-third color to the open perfect intervals." },
  { id: "lesson-6", title: "Lesson 6", mode: "harmonic", hint: "Major Third harmonic", count: 4, adds: "Major Third in harmonic intervals", why: "Makes the student hear major-third color when the notes blend together." },
  { id: "lesson-7", title: "Lesson 7", mode: "direction", hint: "up or down", count: 4, adds: "Ascending vs descending", why: "Keeps the interval set familiar while asking which direction the melody moved." },
  { id: "lesson-8", title: "Lesson 8", mode: "comparison", hint: "wider interval", count: 4, adds: "Interval comparison", why: "Builds relative hearing by asking which of two intervals is wider." },
  { id: "lesson-9", title: "Lesson 9", mode: "single", hint: "full set single", count: 99, adds: "Full section set in single intervals", why: "Reviews every interval introduced in this section as melodic single intervals." },
  { id: "lesson-10", title: "Lesson 10", mode: "harmonic", hint: "full harmonic", count: 99, adds: "Full section set in harmonic intervals", why: "Reviews every interval introduced in this section as simultaneous sounds." },
  { id: "lesson-11", title: "Lesson 11", mode: "direction", hint: "full direction", count: 99, adds: "Full section set ascending and descending", why: "Tests direction across the complete interval set for this section." },
  { id: "lesson-12", title: "Lesson 12", mode: "comparison", hint: "full comparison", count: 99, adds: "Full section set comparison", why: "Combines the section's intervals in wider-or-narrower comparison drills." },
];
const SECTION_LESSONS = Object.fromEntries(Object.entries(SECTION_INTERVALS).map(([sectionId, intervalIds]) => [
  sectionId,
  LESSON_BLUEPRINTS.map((phase) => ({
    ...phase,
    moduleId: sectionId,
    intervalIds: intervalIds.slice(0, Math.min(phase.count, intervalIds.length)),
    fixedRootMidi: sectionId === "section-3" && phase.id === "lesson-12" ? null : 60,
  })),
]));
SECTION_LESSONS["section-4"] = [
  { id: "lesson-1", title: "Lesson 1", mode: "chord", hint: "Major / Minor", chordIds: ["majorTriad", "minorTriad"], adds: "Major and minor triads", why: "Starts with the basic happy-versus-sad color that anchors triad hearing." },
  { id: "lesson-2", title: "Lesson 2", mode: "chord", hint: "Minor 7", chordIds: ["majorTriad", "minorTriad", "minorSeventh"], adds: "Minor seventh chords", why: "Adds a warmer four-note sound after the student knows plain minor." },
  { id: "lesson-3", title: "Lesson 3", mode: "chord", hint: "Dominant", chordIds: ["majorTriad", "minorTriad", "minorSeventh", "dominantSeventh"], adds: "Dominant seventh chords", why: "Introduces the bluesy, wants-to-resolve dominant sound." },
  { id: "lesson-4", title: "Lesson 4", mode: "chord", hint: "Sus4", chordIds: ["majorTriad", "minorTriad", "minorSeventh", "dominantSeventh", "suspendedFourth"], adds: "Suspended fourth chords", why: "Teaches the floating sound of replacing the third with a fourth." },
  { id: "lesson-5", title: "Lesson 5", mode: "chord", hint: "Sus2", chordIds: ["majorTriad", "minorTriad", "minorSeventh", "dominantSeventh", "suspendedFourth", "suspendedSecond"], adds: "Suspended second chords", why: "Adds the more open suspended color that sits lower than suspended fourth." },
  { id: "lesson-6", title: "Lesson 6", mode: "chord", hint: "1st Inv.", chordIds: ["majorTriad", "minorTriad", "suspendedFourth", "suspendedSecond", "firstInversion"], adds: "First inversion triads", why: "Moves the third into the bass so students can hear that a familiar chord can stand on a new bottom note." },
  { id: "lesson-7", title: "Lesson 7", mode: "chord", hint: "2nd Inv.", chordIds: ["majorTriad", "minorTriad", "firstInversion", "secondInversion"], adds: "Second inversion triads", why: "Moves the fifth into the bass and completes the basic triad inversion set." },
  { id: "lesson-8", title: "Lesson 8", mode: "chord", hint: "Slash", chordIds: ["majorTriad", "minorTriad", "firstInversion", "secondInversion", "slashChord"], adds: "Slash chords", why: "Introduces a chord over a specific bass note, a common pop and worship keyboard sound." },
  { id: "lesson-9", title: "Lesson 9", mode: "chord", hint: "Dim", chordIds: ["majorTriad", "minorTriad", "dominantSeventh", "diminishedTriad"], adds: "Diminished triads", why: "Adds the tight unstable triad built from stacked minor thirds." },
  { id: "lesson-10", title: "Lesson 10", mode: "chord", hint: "Aug", chordIds: ["majorTriad", "minorTriad", "diminishedTriad", "augmentedTriad"], adds: "Augmented triads", why: "Contrasts diminished tension with the wider, dreamy sound of an augmented triad." },
  { id: "lesson-11", title: "Lesson 11", mode: "chord", hint: "9th", chordIds: ["majorTriad", "minorTriad", "dominantSeventh", "minorSeventh", "ninthChord"], adds: "Ninth chords", why: "Adds a color tone above the seventh so students can hear richer harmony without losing the chord root." },
  { id: "lesson-12", title: "Lesson 12", mode: "chord", hint: "11th / 13th", chordIds: ["majorTriad", "minorTriad", "dominantSeventh", "minorSeventh", "ninthChord", "eleventhChord", "thirteenthChord"], adds: "Eleventh and thirteenth chords", why: "Finishes the section with broad extended harmony used in jazz, soul, film, and modern pop." },
].map((lesson) => ({
  ...lesson,
  moduleId: "section-4",
  fixedRootMidi: 60,
}));
const DEFAULT_SECTION_ID = "section-1";
const DEFAULT_SECTION_LESSON = SECTION_LESSONS[DEFAULT_SECTION_ID][0];
const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const WHITE_NOTES = new Set(["C", "D", "E", "F", "G", "A", "B"]);
const BLACK_OFFSETS = {
  "C#4": 4.9,
  "D#4": 12.05,
  "F#4": 26.35,
  "G#4": 33.48,
  "A#4": 40.62,
  "C#5": 54.88,
  "D#5": 62.03,
  "F#5": 76.32,
  "G#5": 83.46,
  "A#5": 90.6,
};
const SOUND_FONT = {
  id: "acoustic_grand_piano",
  globalName: "acoustic_grand_piano",
  label: "Acoustic Grand Piano",
  gain: 0.85,
  release: 1.2,
  defaultVelocity: 0.72,
};
const PRACTICE_TOTAL = 20;
const TEST_TOTAL = 20;
const GUIDED_PRACTICE_TOTAL = 10;
const LOOP_STORAGE_KEY = "bardsCorner.earTraining.loop.v1";
const AUTO_ADVANCE_STORAGE_KEY = "bardsCorner.earTraining.autoAdvance.v1";
const NOTE_KEY_MIDI = new Map([
  ["a", 60],
  ["s", 62],
  ["d", 64],
  ["f", 65],
  ["g", 67],
  ["h", 69],
  ["j", 71],
  ["k", 72],
]);
const soundfontScriptPromises = new Map();
const audioState = {
  sampler: null,
  ready: false,
  loadPromise: null,
};

const state = {
  summary: null,
  lesson: null,
  concepts: [],
  currentPrompt: null,
  moduleId: DEFAULT_SECTION_ID,
  lessonConfig: DEFAULT_SECTION_LESSON,
  stage: "ready",
  promptStartedAt: 0,
  answered: false,
  selectedInterval: null,
  selectedAt: 0,
  practiceCompleted: 0,
  practiceCorrect: 0,
  practiceResponseMs: 0,
  testCompleted: 0,
  correct: 0,
  totalResponseMs: 0,
  confidenceTotal: 0,
  conceptStats: new Map(),
  promptPlaybackToken: 0,
  promptPlaybackTimers: [],
  loopEnabled: false,
  loopTimer: 0,
  autoAdvanceEnabled: true,
  autoAdvanceTimer: 0,
  recentIntervalIds: [],
};

function $(id) {
  return document.getElementById(id);
}

function handleModuleDeepLink() {
  const params = new URLSearchParams(window.location.search);
  const moduleId = params.get("module") || params.get("section");
  const module = MODULES.find((item) => item.id === moduleId);

  if (!module || !window.location.pathname.match(/^\/ear-training\/(?:default\.htm)?$/)) {
    return;
  }

  window.location.replace(module.href);
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.error) {
    throw new Error(body.error || `Request failed: ${response.status}`);
  }
  return body;
}

function starString(stars) {
  return `${"★".repeat(stars)}${"☆".repeat(3 - stars)}`;
}

function scoreText(score) {
  return `${Math.round(Number(score) || 0)}%`;
}

function clampPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return 0;
  }
  return Math.max(0, Math.min(100, number));
}

function noteName(midi) {
  const name = NOTE_NAMES[((midi % 12) + 12) % 12];
  const octave = Math.floor(midi / 12) - 1;
  return `${name}${octave}`;
}

function soundfontScriptSrc() {
  return `/sounds/fatboy/${SOUND_FONT.globalName}-ogg.js`;
}

async function ensureSoundfontData() {
  if (window.MIDI?.Soundfont?.[SOUND_FONT.globalName]) {
    return;
  }

  const src = soundfontScriptSrc();
  const existing = soundfontScriptPromises.get(src);
  if (existing) {
    await existing;
    return;
  }

  const loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      if (window.MIDI?.Soundfont?.[SOUND_FONT.globalName]) {
        resolve();
        return;
      }
      reject(new Error(`Sound bank loaded without data: ${SOUND_FONT.label}`));
    };
    script.onerror = () => reject(new Error(`Could not load sound bank: ${SOUND_FONT.label}`));
    document.head.append(script);
  });
  soundfontScriptPromises.set(src, loadPromise);
  await loadPromise;
}

async function ensureSampler() {
  await Tone.start();
  if (audioState.ready && audioState.sampler) {
    return audioState;
  }
  if (audioState.loadPromise) {
    await audioState.loadPromise;
    return audioState;
  }

  await ensureSoundfontData();
  const soundfont = window.MIDI?.Soundfont?.[SOUND_FONT.globalName];
  if (!soundfont) {
    throw new Error(`Sound bank is not loaded: ${SOUND_FONT.label}`);
  }

  const output = new Tone.Gain(SOUND_FONT.gain).toDestination();
  audioState.sampler = new Tone.Sampler({
    urls: { ...soundfont },
    attack: 0,
    release: SOUND_FONT.release,
    onload() {
      audioState.ready = true;
    },
  }).connect(output);
  audioState.loadPromise = Tone.loaded().then(() => {
    audioState.ready = true;
    return audioState;
  });
  await audioState.loadPromise;
  return audioState;
}

async function playMidi(midi, duration = 0.72, delay = 0) {
  const samplerState = await ensureSampler();
  samplerState.sampler?.triggerAttackRelease(
    Tone.Frequency(midi, "midi").toNote(),
    duration,
    Tone.now() + delay,
    SOUND_FONT.defaultVelocity,
  );
}

function flashPianoKey(midi) {
  const key = document.querySelector(`.piano-key[data-midi="${midi}"]`);
  if (!key) {
    return;
  }
  key.classList.remove("is-flashing");
  void key.offsetWidth;
  key.classList.add("is-flashing");
  window.setTimeout(() => key.classList.remove("is-flashing"), 220);
}

function stopPromptAudio() {
  state.promptPlaybackToken += 1;
  state.promptPlaybackTimers.forEach((timer) => window.clearTimeout(timer));
  state.promptPlaybackTimers = [];
  if (state.loopTimer) {
    window.clearTimeout(state.loopTimer);
    state.loopTimer = 0;
  }
  const sampler = audioState.sampler;
  if (sampler) {
    try {
      sampler.releaseAll?.(Tone.now());
    } catch {
      // Loaded samplers can briefly reject release calls while the audio graph settles.
    }
  }
}

function clearAutoAdvanceTimer() {
  if (state.autoAdvanceTimer) {
    window.clearTimeout(state.autoAdvanceTimer);
    state.autoAdvanceTimer = 0;
  }
}

function suspendCurrentLoop() {
  if (state.loopTimer) {
    window.clearTimeout(state.loopTimer);
    state.loopTimer = 0;
  }
  state.promptPlaybackToken += 1;
}

function scheduleLoopReplay(prompt, token) {
  if (!state.loopEnabled || !prompt || token !== state.promptPlaybackToken) {
    return;
  }
  state.loopTimer = window.setTimeout(() => {
    if (!state.loopEnabled || state.currentPrompt !== prompt || token !== state.promptPlaybackToken) {
      return;
    }
    playPrompt(prompt).catch((error) => {
      $("statusLine").textContent = error.message || "Could not load that sound.";
    });
  }, 1900);
}

async function playPrompt(prompt) {
  stopPromptAudio();
  const token = state.promptPlaybackToken;
  if (state.lessonConfig.mode === "chord") {
    await Promise.all(prompt.chordMidis.map((midi) => playMidi(midi, 1.2, 0)));
    scheduleLoopReplay(prompt, token);
    return;
  }
  if (state.lessonConfig.mode === "harmonic") {
    await Promise.all([
      playMidi(prompt.rootMidi, 0.9, 0),
      playMidi(prompt.targetMidi, 0.9, 0),
    ]);
    scheduleLoopReplay(prompt, token);
    return;
  }
  if (state.lessonConfig.mode === "comparison") {
    await playMidi(prompt.rootMidi, 0.45, 0);
    const timers = [
      window.setTimeout(() => {
        if (token !== state.promptPlaybackToken) {
          return;
        }
        playMidi(prompt.targetMidi, 0.55, 0).catch(() => {});
      }, 520),
      window.setTimeout(() => {
        if (token !== state.promptPlaybackToken) {
          return;
        }
        playMidi(prompt.secondRootMidi, 0.45, 0).catch(() => {});
      }, 1250),
      window.setTimeout(() => {
        if (token !== state.promptPlaybackToken) {
          return;
        }
        playMidi(prompt.secondTargetMidi, 0.55, 0).catch((error) => {
          $("statusLine").textContent = error.message || "Could not load that sound.";
        });
        scheduleLoopReplay(prompt, token);
      }, 1770),
    ];
    state.promptPlaybackTimers.push(...timers);
    return;
  }
  await playMidi(prompt.rootMidi, 0.55, 0);
  const timer = window.setTimeout(() => {
    if (token !== state.promptPlaybackToken) {
      return;
    }
    playMidi(prompt.targetMidi, 0.72, 0).catch((error) => {
      $("statusLine").textContent = error.message || "Could not load that sound.";
    });
    scheduleLoopReplay(prompt, token);
  }, 680);
  state.promptPlaybackTimers.push(timer);
}

function twoOctaveNotes() {
  return Array.from({ length: 24 }, (_unused, index) => 60 + index);
}

function renderPiano(container, onClick) {
  if (!container) {
    return;
  }
  const notes = twoOctaveNotes();
  const whites = document.createElement("div");
  whites.className = "piano-whites";
  const blacks = [];

  notes.forEach((midi) => {
    const label = noteName(midi);
    const pitch = label.replace(/\d+$/, "");
    const button = document.createElement("button");
    button.type = "button";
    button.className = `piano-key ${WHITE_NOTES.has(pitch) ? "white" : "black"}`;
    button.dataset.midi = String(midi);
    button.textContent = label;
    button.setAttribute("aria-label", label);
    button.addEventListener("click", () => onClick?.(midi));
    if (WHITE_NOTES.has(pitch)) {
      whites.append(button);
    } else {
      button.style.left = `${BLACK_OFFSETS[label] || 0}%`;
      blacks.push(button);
    }
  });

  container.replaceChildren(whites, ...blacks);
}

function highlightPiano(rootMidi, targetMidi) {
  const highlightedMidis = Array.isArray(rootMidi) ? new Set(rootMidi) : null;
  document.querySelectorAll(".piano-key").forEach((key) => {
    const midi = Number(key.dataset.midi);
    key.classList.toggle("is-root", highlightedMidis ? midi === targetMidi : midi === rootMidi);
    key.classList.toggle("is-highlighted", highlightedMidis ? highlightedMidis.has(midi) : midi === targetMidi || midi === rootMidi);
  });
}

function shouldShowPromptNotes() {
  return state.stage === "practice" && state.practiceCompleted < GUIDED_PRACTICE_TOTAL;
}

function landingModulesFromSummary(summary) {
  if (summary?.modules?.length) {
    return summary.modules.map((module) => ({
      ...(MODULES.find((item) => item.id === module.id) || {}),
      ...module,
      href: (MODULES.find((item) => item.id === module.id) || {}).href || module.href || "/ear-training/",
    }));
  }
  return MODULES.map((module) => ({
    ...module,
    score: 0,
    stars: 0,
    completed: false,
    completedLessons: 0,
    lessons: Array.from({ length: 12 }, (_unused, index) => ({
      lessonId: `lesson-${index + 1}`,
      bestScore: 0,
      stars: 0,
    })),
  }));
}

function renderLanding(summary) {
  const grid = $("moduleGrid");
  const weakList = $("weakList");
  if (!grid) {
    return;
  }

  grid.replaceChildren(...landingModulesFromSummary(summary).map((module) => {
    const card = document.createElement("a");
    card.className = "module-card";
    card.href = module.href;
    card.innerHTML = `
      <div class="module-title-row">
        <h2 class="module-title">${module.title}</h2>
        <span class="score-pill">${scoreText(module.score)}</span>
      </div>
      <p class="module-description">${module.description}</p>
      <div class="metric-row mt-3">
        <span class="stars" aria-label="${module.stars} stars">${starString(module.stars)}</span>
        <span class="text-body-secondary small">${module.completedLessons || 0}/12 lessons</span>
      </div>
    `;
    return card;
  }));

  if (weakList) {
    const concepts = summary?.weakConcepts || [];
    weakList.replaceChildren(...(concepts.length ? concepts : [{ label: "No weak spots yet", accuracy: 0 }]).slice(0, 6).map((concept) => {
      const item = document.createElement("li");
      const accuracy = clampPercent(concept.accuracy);
      item.innerHTML = `
        <div class="weak-skill-main">
          <span>${concept.label}</span>
          <div class="weak-skill-meter" aria-hidden="true"><span style="width: ${accuracy}%"></span></div>
        </div>
        <strong>${scoreText(accuracy)}</strong>
      `;
      return item;
    }));
  }
}

function renderLessonPicker(moduleSummary) {
  const grid = $("lessonGrid");
  if (!grid || !moduleSummary) {
    return;
  }
  const lessons = SECTION_LESSONS[moduleSummary.id] || SECTION_LESSONS[DEFAULT_SECTION_ID];
  const nextLessonIndex = (moduleSummary.lessons || []).findIndex((phase) => !phase.completedAt);
  grid.replaceChildren(...moduleSummary.lessons.map((phase, index) => {
    const button = document.createElement("a");
    const lessonId = `lesson-${index + 1}`;
    const lessonHref = `/ear-training/${moduleSummary.id}/${lessonId}/intro.htm`;
    const completed = Boolean(phase.completedAt);
    const next = index === (nextLessonIndex === -1 ? 0 : nextLessonIndex);
    button.className = `btn btn-outline-light lesson-button${completed ? " is-complete" : ""}${next ? " is-next" : ""}`;
    button.href = lessonHref;
    button.innerHTML = `
      <span>Lesson ${index + 1}</span>
      <span class="lesson-hint">${lessons[index]?.hint || ""}</span>
      <span class="lesson-score">${scoreText(phase.bestScore)} <span class="lesson-stars">${starString(phase.stars)}</span></span>
    `;
    return button;
  }));
}

function skillListItem(concept) {
  const accuracy = clampPercent(concept.accuracy);
  const item = document.createElement("li");
  item.innerHTML = `
    <div class="weak-skill-main">
      <span>${concept.label}</span>
      <div class="weak-skill-meter" aria-hidden="true"><span style="width: ${accuracy}%"></span></div>
    </div>
    <strong>${scoreText(accuracy)}</strong>
  `;
  return item;
}

function sectionIdFromPath() {
  return window.location.pathname.match(/\/ear-training\/(section-\d+)\//)?.[1] || DEFAULT_SECTION_ID;
}

function renderSectionOverview(summary) {
  const sectionId = sectionIdFromPath();
  const moduleSummary = summary?.modules?.find((module) => module.id === sectionId)
    || landingModulesFromSummary(null).find((module) => module.id === sectionId);
  const lessons = SECTION_LESSONS[sectionId] || SECTION_LESSONS[DEFAULT_SECTION_ID];

  renderLessonPicker(moduleSummary);

  const lessonExplanationList = $("lessonExplanationList");
  if (lessonExplanationList) {
    lessonExplanationList.replaceChildren(...lessons.map((phase, index) => {
      const lessonProgress = moduleSummary?.lessons?.[index];
      const completed = Boolean(lessonProgress?.completedAt);
      const item = document.createElement("a");
      item.className = `lesson-explanation${completed ? " is-complete" : ""}`;
      item.href = `/ear-training/${sectionId}/${phase.id}/intro.htm`;
      item.innerHTML = `
        <div class="lesson-explanation-topline">
          <h3>${phase.title}</h3>
          <span>${scoreText(lessonProgress?.bestScore || 0)} <span class="lesson-stars">${starString(lessonProgress?.stars || 0)}</span></span>
        </div>
        <p><strong>Adds:</strong> ${phase.adds}</p>
        <p>${phase.why}</p>
      `;
      return item;
    }));
  }

  const attempts = (moduleSummary?.lessons || []).reduce((sum, phase) => sum + (Number(phase.attempts) || 0), 0);
  const completed = Number(moduleSummary?.completedLessons) || 0;
  const bestLesson = (moduleSummary?.lessons || []).reduce((best, lesson) => (
    Number(lesson.bestScore) > Number(best?.bestScore || 0) ? lesson : best
  ), null);
  const moduleStats = $("moduleStats");
  if (moduleStats) {
    moduleStats.innerHTML = `
      <div><strong>${scoreText(moduleSummary?.score || 0)}</strong><span>module score</span></div>
      <div><strong>${attempts}</strong><span>attempts</span></div>
      <div><strong>${completed}/12</strong><span>lessons complete</span></div>
      <div><strong>${bestLesson ? scoreText(bestLesson.bestScore) : "0%"}</strong><span>best lesson</span></div>
    `;
  }

  const moduleConcepts = (summary?.weakConcepts || [])
    .filter((concept) => concept.moduleId === sectionId || concept.module_id === sectionId);
  const sorted = [...moduleConcepts].sort((a, b) => Number(b.accuracy) - Number(a.accuracy));
  const strongest = sorted.filter((concept) => Number(concept.attempts) > 0).slice(0, 5);
  const weakest = [...moduleConcepts]
    .filter((concept) => Number(concept.attempts) > 0)
    .sort((a, b) => Number(a.accuracy) - Number(b.accuracy))
    .slice(0, 5);
  $("strongSkillList")?.replaceChildren(...(strongest.length ? strongest : [{ label: "No attempts yet", accuracy: 0 }]).map(skillListItem));
  $("weakSkillList")?.replaceChildren(...(weakest.length ? weakest : [{ label: "No attempts yet", accuracy: 0 }]).map(skillListItem));
}

function lessonIdFromPath() {
  const match = window.location.pathname.match(/\/section-\d+\/(lesson-\d+)\//);
  return match ? match[1] : "lesson-1";
}

function sectionLessonConfig(lessonId = lessonIdFromPath(), sectionId = sectionIdFromPath()) {
  const lessons = SECTION_LESSONS[sectionId] || SECTION_LESSONS[DEFAULT_SECTION_ID];
  return lessons.find((phase) => phase.id === lessonId) || lessons[0];
}

function lessonIntervals() {
  return (state.lessonConfig.intervalIds || [])
    .map((id) => INTERVAL_BY_ID.get(id))
    .filter(Boolean);
}

function lessonChords() {
  return (state.lessonConfig.chordIds || [])
    .map((id) => CHORD_BY_ID.get(id))
    .filter(Boolean);
}

function lessonConcepts() {
  return state.lessonConfig.mode === "chord" ? lessonChords() : lessonIntervals();
}

function lessonSkillList() {
  return document.querySelector("#lessonNotesTitle + .weak-list");
}

function renderLessonSkillList() {
  const list = lessonSkillList();
  if (!list) {
    return;
  }

  list.replaceChildren(...lessonConcepts().map((concept) => {
    const stat = state.conceptStats.get(concept.id);
    const savedConcept = state.concepts.find((item) => item.conceptId === concept.id);
    const attempts = stat?.attempts || 0;
    const correct = stat?.correct || 0;
    const incorrect = Math.max(0, attempts - correct);
    const accuracy = attempts ? (correct / attempts) * 100 : savedConcept?.accuracy || 0;
    const item = document.createElement("li");
    item.innerHTML = `
      <div class="weak-skill-main">
        <span>${concept.label}</span>
        <div class="weak-skill-meter" aria-hidden="true"><span style="width: ${clampPercent(accuracy)}%"></span></div>
      </div>
      <div class="skill-result-counts" aria-label="${correct} correct, ${incorrect} incorrect">
        <span class="is-good">${correct}</span>
        <span class="is-bad">${incorrect}</span>
      </div>
    `;
    return item;
  }));
}

function dueWeight(item) {
  const concept = state.concepts.find((conceptItem) => conceptItem.conceptId === item.id);
  if (!concept) {
    return 1;
  }
  const weakBoost = Math.max(0, 100 - concept.accuracy) / 28;
  const dueBoost = !concept.nextReviewAt || new Date(concept.nextReviewAt).getTime() <= Date.now() ? 1.2 : 0;
  return 1 + weakBoost + dueBoost + (concept.weakCount || 0) * 0.08;
}

function weightedConcept(items = lessonConcepts()) {
  const concepts = items.length ? items : INTERVALS.slice(0, 2);
  const lastTwo = state.recentIntervalIds.slice(-2);
  const blockedId = lastTwo.length === 2 && lastTwo[0] === lastTwo[1] && concepts.length > 1
    ? lastTwo[0]
    : "";
  const availableConcepts = blockedId
    ? concepts.filter((concept) => concept.id !== blockedId)
    : concepts;
  const weighted = availableConcepts.map((concept) => ({
    concept,
    weight: dueWeight(concept),
  }));
  const total = weighted.reduce((sum, item) => sum + item.weight, 0);
  let cursor = Math.random() * total;
  for (const item of weighted) {
    cursor -= item.weight;
    if (cursor <= 0) {
      return item.concept;
    }
  }
  return concepts[0];
}

function weightedInterval() {
  return weightedConcept(lessonIntervals());
}

function chordRootFor(chord, preferredRootMidi) {
  const lowNote = Math.min(...chord.intervals.map((offset) => preferredRootMidi + offset));
  const highNote = Math.max(...chord.intervals.map((offset) => preferredRootMidi + offset));
  if (lowNote < 60) {
    return preferredRootMidi + 12;
  }
  if (highNote > 83) {
    return preferredRootMidi - 12;
  }
  return preferredRootMidi;
}

function makePrompt() {
  const concept = state.lessonConfig.mode === "chord" ? weightedConcept(lessonChords()) : weightedInterval();
  state.recentIntervalIds.push(concept.id);
  state.recentIntervalIds = state.recentIntervalIds.slice(-4);
  let rootMidi = Number.isInteger(state.lessonConfig.fixedRootMidi)
    ? state.lessonConfig.fixedRootMidi
    : 60 + Math.floor(Math.random() * 12);
  if (state.lessonConfig.mode === "chord") {
    rootMidi = chordRootFor(concept, rootMidi);
    return {
      chord: concept,
      rootMidi,
      chordMidis: concept.intervals.map((offset) => rootMidi + offset),
    };
  }
  const interval = concept;
  const direction = state.lessonConfig.mode === "direction" && Math.random() < 0.5 ? "descending" : "ascending";
  if (direction === "descending" && rootMidi - interval.semitones < 60) {
    rootMidi = Math.min(83, rootMidi + 12);
  }
  if (state.lessonConfig.mode === "comparison") {
    const secondInterval = weightedInterval();
    const secondRootMidi = Number.isInteger(state.lessonConfig.fixedRootMidi)
      ? rootMidi + 12
      : 60 + Math.floor(Math.random() * 12);
    const comparison = interval.semitones === secondInterval.semitones
      ? "same"
      : interval.semitones > secondInterval.semitones
        ? "first"
        : "second";
    return {
      interval,
      secondInterval,
      comparison,
      rootMidi,
      targetMidi: rootMidi + interval.semitones,
      secondRootMidi,
      secondTargetMidi: secondRootMidi + secondInterval.semitones,
    };
  }
  return {
    interval,
    direction,
    rootMidi,
    targetMidi: direction === "descending" ? rootMidi - interval.semitones : rootMidi + interval.semitones,
  };
}

function currentMode() {
  if (state.stage === "test" || state.stage === "test-summary") {
    return "test";
  }
  return "practice";
}

function promptNumber() {
  return currentMode() === "practice"
    ? state.practiceCompleted + 1
    : state.testCompleted + 1;
}

function practiceAccuracy() {
  return state.practiceCompleted
    ? (state.practiceCorrect / state.practiceCompleted) * 100
    : 0;
}

function testAccuracy() {
  return state.testCompleted
    ? (state.correct / state.testCompleted) * 100
    : 0;
}

function averageResponseMs(totalResponseMs, totalAnswers) {
  return Math.round(totalResponseMs / Math.max(1, totalAnswers));
}

function checkpointRecommendation() {
  const accuracy = practiceAccuracy();
  const avgMs = averageResponseMs(state.practiceResponseMs, state.practiceCompleted);
  if (accuracy >= 85 && avgMs <= 3500) {
    return "Great training run. Start the test.";
  }
  return "Repeat the training once more before testing.";
}

function renderTrainer() {
  const mode = currentMode();
  const complete = state.stage === "test-summary";
  const checkpoint = state.stage === "practice-summary";
  const blindNotice = state.stage === "blind-practice-notice";
  const totalDone = state.practiceCompleted + state.testCompleted;
  const totalQuestions = PRACTICE_TOTAL + TEST_TOTAL;
  const progressPercent = Math.min(100, (totalDone / totalQuestions) * 100);

  $("modeLabel").textContent = complete ? "Complete" : checkpoint ? "Checkpoint" : blindNotice ? "No Hints" : mode === "practice" ? "Practice" : "Test";
  $("questionCount").textContent = complete ? `${totalQuestions}/${totalQuestions}` : checkpoint || blindNotice ? "10/20" : `${promptNumber()}/20`;
  $("trainerProgressBar").style.width = `${progressPercent}%`;
  $("scoreReadout").textContent = mode === "practice" && !complete
    ? `Practice: ${state.practiceCorrect} correct, ${Math.max(0, state.practiceCompleted - state.practiceCorrect)} incorrect`
    : `Test: ${state.correct} correct, ${Math.max(0, state.testCompleted - state.correct)} incorrect`;
  $("nextButton").textContent = state.selectedInterval ? "Next" : "Choose an answer";
  $("nextButton").disabled = !state.selectedInterval;
  $("nextButton").classList.toggle("d-none", checkpoint || blindNotice || complete || state.stage === "ready");
  $("finishButton").classList.toggle("d-none", !complete);

  if (checkpoint) {
    renderPracticeCheckpoint();
    return;
  }

  if (blindNotice) {
    renderBlindPracticeNotice();
    return;
  }

  if (complete) {
    renderTestSummary();
    return;
  }

  $("statusLine").textContent = state.stage === "ready"
    ? "Press Start when you are ready."
    : mode === "practice"
      ? shouldShowPromptNotes()
        ? "Pick an answer. You can change it before pressing Next."
        : "Pick an answer. The note highlights are hidden now."
      : "Pick an answer, then press Next to lock it in.";
}

function renderAnswerButtons() {
  const grid = $("answerGrid");
  if (!grid) {
    return;
  }
  const answers = state.lessonConfig.mode === "direction"
    ? [
        { id: "ascending", label: "Ascending", key: "1" },
        { id: "descending", label: "Descending", key: "2" },
      ]
    : state.lessonConfig.mode === "comparison"
      ? [
          { id: "first", label: "First is wider", key: "1" },
          { id: "second", label: "Second is wider", key: "2" },
          { id: "same", label: "Same size", key: "3" },
        ]
      : state.lessonConfig.mode === "chord"
        ? lessonChords()
        : lessonIntervals();
  grid.replaceChildren(...answers.map((answer) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn btn-outline-light";
    button.dataset.intervalId = answer.id;
    button.dataset.answerKey = answer.key;
    const keyHint = answer.key
      ? `<span class="answer-key-hint" aria-hidden="true">${answer.key}</span>`
      : "";
    button.innerHTML = `<span>${answer.shortLabel || answer.label}</span>${keyHint}`;
    button.addEventListener("click", () => answerPrompt(answer));
    return button;
  }));
}

function showConfetti(button) {
  if (!button) {
    return;
  }
  const confetti = document.createElement("span");
  confetti.className = "answer-confetti";
  confetti.setAttribute("aria-hidden", "true");
  for (let index = 0; index < 12; index += 1) {
    const piece = document.createElement("span");
    piece.style.setProperty("--x", `${Math.cos(index * 0.9) * (34 + index * 2.2)}px`);
    piece.style.setProperty("--y", `${Math.sin(index * 0.9) * 24 - 42}px`);
    piece.style.setProperty("--hue", `${48 + index * 29}`);
    confetti.append(piece);
  }
  button.append(confetti);
  window.setTimeout(() => confetti.remove(), 720);
}

function conceptStat(interval) {
  const conceptId = state.lessonConfig.mode === "chord"
    ? interval.id
    : state.lessonConfig.mode === "comparison" && state.currentPrompt?.secondInterval
    ? `${interval.id}-vs-${state.currentPrompt.secondInterval.id}`
    : state.lessonConfig.mode === "direction"
      ? `${interval.id}-${state.currentPrompt?.direction || "direction"}`
      : interval.id;
  const label = state.lessonConfig.mode === "chord"
    ? interval.label
    : state.lessonConfig.mode === "comparison" && state.currentPrompt?.secondInterval
    ? `${interval.label} vs ${state.currentPrompt.secondInterval.label}`
    : state.lessonConfig.mode === "direction"
      ? `${interval.label} ${state.currentPrompt?.direction || ""}`.trim()
      : interval.label;
  if (!state.conceptStats.has(conceptId)) {
    state.conceptStats.set(conceptId, {
      conceptId,
      conceptType: state.lessonConfig.mode,
      label,
      attempts: 0,
      correct: 0,
      responseMsTotal: 0,
      confidenceTotal: 0,
    });
  }
  return state.conceptStats.get(conceptId);
}

function answerPrompt(selected) {
  if (!state.currentPrompt || state.stage === "practice-summary" || state.stage === "test-summary") {
    return;
  }
  if (state.stage === "test" && state.selectedInterval) {
    return;
  }
  const expectedAnswer = state.lessonConfig.mode === "direction"
    ? state.currentPrompt.direction
    : state.lessonConfig.mode === "comparison"
      ? state.currentPrompt.comparison
      : state.lessonConfig.mode === "chord"
        ? state.currentPrompt.chord.id
        : state.currentPrompt.interval.id;
  const correct = state.lessonConfig.mode === "single" || state.lessonConfig.mode === "harmonic"
    ? selected.id === expectedAnswer || selected.semitones === state.currentPrompt.interval.semitones
    : selected.id === expectedAnswer;
  state.selectedInterval = selected;
  state.selectedAt = Date.now();

  document.querySelectorAll("[data-interval-id]").forEach((button) => {
    const id = button.dataset.intervalId;
    const selectedButton = id === selected.id;
    button.classList.toggle("is-selected", selectedButton);
    button.classList.toggle("is-correct", selectedButton && correct);
    button.classList.toggle("is-wrong", selectedButton && !correct);
  });

  $("answerResult").textContent = correct
    ? "Correct"
    : currentMode() === "practice"
      ? "Try another answer or press Next to keep it."
      : "Selected";

  if (correct) {
    suspendCurrentLoop();
    showConfetti(document.querySelector(`[data-interval-id="${selected.id}"]`));
    scheduleAutoAdvance();
  }
  renderTrainer();
  if (state.stage === "test") {
    document.querySelectorAll("[data-interval-id]").forEach((button) => {
      button.disabled = true;
    });
    commitSelectedAnswer();
  }
}

function scheduleAutoAdvance() {
  clearAutoAdvanceTimer();
  if (!state.autoAdvanceEnabled || !state.currentPrompt) {
    return;
  }
  const prompt = state.currentPrompt;
  state.autoAdvanceTimer = window.setTimeout(() => {
    if (state.currentPrompt !== prompt || !state.selectedInterval) {
      return;
    }
    commitSelectedAnswer();
  }, 2000);
}

function commitSelectedAnswer() {
  if (!state.currentPrompt || !state.selectedInterval) {
    return;
  }
  clearAutoAdvanceTimer();

  const elapsed = Math.max(1, state.selectedAt - state.promptStartedAt);
  const expectedAnswer = state.lessonConfig.mode === "direction"
    ? state.currentPrompt.direction
    : state.lessonConfig.mode === "comparison"
      ? state.currentPrompt.comparison
      : state.lessonConfig.mode === "chord"
        ? state.currentPrompt.chord.id
        : state.currentPrompt.interval.id;
  const correct = state.lessonConfig.mode === "single" || state.lessonConfig.mode === "harmonic"
    ? state.selectedInterval.id === expectedAnswer || state.selectedInterval.semitones === state.currentPrompt.interval.semitones
    : state.selectedInterval.id === expectedAnswer;
  const confidence = correct ? Math.max(55, 100 - elapsed / 55) : Math.max(20, 72 - elapsed / 80);
  const mode = currentMode();
  const stat = conceptStat(state.currentPrompt.chord || state.currentPrompt.interval);

  state.answered = true;
  stat.attempts += 1;
  stat.correct += correct ? 1 : 0;
  stat.responseMsTotal += elapsed;
  stat.confidenceTotal += confidence;
  state.totalResponseMs += elapsed;
  state.confidenceTotal += confidence;

  if (mode === "practice") {
    state.practiceCompleted += 1;
    state.practiceCorrect += correct ? 1 : 0;
    state.practiceResponseMs += elapsed;
    renderLessonSkillList();
    if (state.practiceCompleted === GUIDED_PRACTICE_TOTAL) {
      showBlindPracticeNotice();
      return;
    }
    if (state.practiceCompleted >= PRACTICE_TOTAL) {
      showPracticeCheckpoint();
      return;
    }
  } else {
    state.testCompleted += 1;
    state.correct += correct ? 1 : 0;
    renderLessonSkillList();
    if (state.testCompleted >= TEST_TOTAL) {
      showTestSummary();
      return;
    }
  }

  nextPrompt();
}

async function nextPrompt() {
  clearAutoAdvanceTimer();
  state.stage = currentMode();
  state.currentPrompt = makePrompt();
  state.promptStartedAt = Date.now();
  state.answered = false;
  state.selectedInterval = null;
  state.selectedAt = 0;
  renderAnswerButtons();
  $("answerResult").textContent = "";
  if (shouldShowPromptNotes()) {
    if (state.currentPrompt.chordMidis) {
      highlightPiano(state.currentPrompt.chordMidis, state.currentPrompt.chordMidis[0]);
    } else {
      highlightPiano(state.currentPrompt.rootMidi, state.currentPrompt.targetMidi);
    }
  } else {
    highlightPiano(-1, -1);
  }
  renderTrainer();
  try {
    await playPrompt(state.currentPrompt);
  } catch (error) {
    $("statusLine").textContent = error.message || "Could not load that sound.";
  }
}

function clearPromptUi() {
  clearAutoAdvanceTimer();
  stopPromptAudio();
  state.currentPrompt = null;
  state.selectedInterval = null;
  state.selectedAt = 0;
  $("answerResult").textContent = "";
  $("answerGrid").replaceChildren();
  highlightPiano(-1, -1);
}

function showPracticeCheckpoint() {
  state.stage = "practice-summary";
  clearPromptUi();
  renderTrainer();
}

function showBlindPracticeNotice() {
  state.stage = "blind-practice-notice";
  clearPromptUi();
  renderTrainer();
}

function showTestSummary() {
  state.stage = "test-summary";
  clearPromptUi();
  renderTrainer();
}

function resetTrainingStats() {
  state.currentPrompt = null;
  state.promptStartedAt = 0;
  state.answered = false;
  state.selectedInterval = null;
  state.selectedAt = 0;
  state.practiceCompleted = 0;
  state.practiceCorrect = 0;
  state.practiceResponseMs = 0;
  state.testCompleted = 0;
  state.correct = 0;
  state.totalResponseMs = 0;
  state.confidenceTotal = 0;
  state.conceptStats = new Map();
  state.recentIntervalIds = [];
}

async function restartTraining() {
  resetTrainingStats();
  state.stage = "practice";
  $("startButton").classList.add("d-none");
  setPlaybackControlsVisible(true);
  await nextPrompt();
}

async function startTest() {
  state.stage = "test";
  state.testCompleted = 0;
  state.correct = 0;
  $("startButton").classList.add("d-none");
  setPlaybackControlsVisible(true);
  await nextPrompt();
}

async function continueBlindPractice() {
  state.stage = "practice";
  await nextPrompt();
}

function renderBlindPracticeNotice() {
  $("answerGrid").innerHTML = `
    <div class="trainer-summary">
      <h3>Hints Off</h3>
      <p>The first 10 practice questions showed the notes on the piano. The last 10 practice questions will still play the interval, but the note highlights are hidden so your ear has to do the work.</p>
      <div class="summary-actions">
        <button id="continueBlindPracticeButton" class="btn btn-primary" type="button">Continue Practice</button>
        <button id="restartGuidedPracticeButton" class="btn btn-outline-light" type="button">Restart Training</button>
      </div>
    </div>
  `;
  $("continueBlindPracticeButton")?.addEventListener("click", continueBlindPractice);
  $("restartGuidedPracticeButton")?.addEventListener("click", restartTraining);
  $("statusLine").textContent = "The piano hints are turning off for the rest of practice.";
}

function renderPracticeCheckpoint() {
  const accuracy = practiceAccuracy();
  const avgMs = averageResponseMs(state.practiceResponseMs, state.practiceCompleted);
  const recommendedTest = accuracy >= 85 && avgMs <= 3500;
  $("answerGrid").innerHTML = `
    <div class="trainer-summary">
      <h3>Practice Complete</h3>
      <div class="summary-metrics">
        <span><strong>${scoreText(accuracy)}</strong> accuracy</span>
        <span><strong>${state.practiceCorrect}/${PRACTICE_TOTAL}</strong> correct</span>
        <span><strong>${avgMs}ms</strong> average</span>
      </div>
      <p>${checkpointRecommendation()}</p>
      <div class="summary-actions">
        <button id="repeatPracticeButton" class="btn ${recommendedTest ? "btn-outline-light" : "btn-primary"}" type="button">Restart Training</button>
        <button id="startTestButton" class="btn ${recommendedTest ? "btn-primary" : "btn-outline-light"}" type="button">Start Test</button>
      </div>
    </div>
  `;
  $("repeatPracticeButton")?.addEventListener("click", restartTraining);
  $("startTestButton")?.addEventListener("click", startTest);
  $("statusLine").textContent = "Take the test when the intervals feel clear.";
}

function renderTestSummary() {
  const accuracy = testAccuracy();
  const avgMs = averageResponseMs(state.totalResponseMs, state.practiceCompleted + state.testCompleted);
  const existingAttempts = Number(state.lesson?.attempts) || 0;
  const existingScore = Number(state.lesson?.bestScore) || 0;
  const score = (state.correct / TEST_TOTAL) * 100;
  const showKeepExisting = existingAttempts > 0;
  const keepScoreNote = showKeepExisting && existingScore > score
    ? `<p>Your existing best is ${scoreText(existingScore)}. You can keep it and leave this run unsaved.</p>`
    : showKeepExisting
      ? `<p>Your existing best is ${scoreText(existingScore)}.</p>`
    : "";
  const keepButton = showKeepExisting
    ? '<button id="keepExistingScoreButton" class="btn btn-outline-light" type="button">Keep Existing Score</button>'
    : "";
  $("answerGrid").innerHTML = `
    <div class="trainer-summary">
      <h3>Test Summary</h3>
      <div class="summary-metrics">
        <span><strong>${scoreText(accuracy)}</strong> score</span>
        <span><strong>${state.correct}/${TEST_TOTAL}</strong> correct</span>
        <span><strong>${avgMs}ms</strong> average</span>
      </div>
      <p>${accuracy >= 85 ? "Great job! You are really getting good at this!" : "This lesson is still developing. A repeat run will help."}</p>
      ${keepScoreNote}
      <div class="summary-actions">
        <button id="saveTestButton" class="btn btn-primary" type="button">Save Score</button>
        ${keepButton}
        <button id="repeatAfterTestButton" class="btn btn-outline-light" type="button">Restart Training</button>
        <a class="btn btn-outline-light" href="/ear-training/${state.moduleId}/">Lessons</a>
      </div>
    </div>
  `;
  $("saveTestButton")?.addEventListener("click", saveLessonAttempt);
  $("keepExistingScoreButton")?.addEventListener("click", () => {
    $("statusLine").textContent = "Existing score kept. This run was not saved.";
  });
  $("repeatAfterTestButton")?.addEventListener("click", restartTraining);
  $("statusLine").textContent = showKeepExisting ? "Save this score or keep your existing best." : "Save your first score to complete this lesson.";
}

function renderShortcutHelp() {
  const statusLine = $("statusLine");
  if (!statusLine || $("shortcutHelp")) {
    return;
  }
  statusLine.insertAdjacentHTML("afterend", `
    <section id="shortcutHelp" class="shortcut-help" aria-label="Keyboard shortcuts">
      <span><kbd>a</kbd>-<kbd>k</kbd> notes</span>
      <span><kbd>2</kbd>-<kbd>8</kbd> major/perfect</span>
      <span><kbd>q</kbd>-<kbd>y</kbd> alternates</span>
      <span><kbd>Space</kbd> replay</span>
      <span><kbd>Enter</kbd> next</span>
    </section>
  `);
}

function loadLoopSetting() {
  state.loopEnabled = window.localStorage.getItem(LOOP_STORAGE_KEY) === "1";
}

function saveLoopSetting() {
  window.localStorage.setItem(LOOP_STORAGE_KEY, state.loopEnabled ? "1" : "0");
}

function loadAutoAdvanceSetting() {
  const stored = window.localStorage.getItem(AUTO_ADVANCE_STORAGE_KEY);
  state.autoAdvanceEnabled = stored === null ? true : stored === "1";
}

function saveAutoAdvanceSetting() {
  window.localStorage.setItem(AUTO_ADVANCE_STORAGE_KEY, state.autoAdvanceEnabled ? "1" : "0");
}

function renderPlaybackControls() {
  const playButton = $("playButton");
  if (!playButton || $("loopReplayInput") || $("autoAdvanceInput")) {
    return;
  }
  playButton.insertAdjacentHTML("afterend", `
    <label class="loop-replay-control${playButton.classList.contains("d-none") ? " d-none" : ""}">
      <input id="loopReplayInput" class="form-check-input" type="checkbox">
      <span>Loop</span>
    </label>
    <label class="loop-replay-control${playButton.classList.contains("d-none") ? " d-none" : ""}">
      <input id="autoAdvanceInput" class="form-check-input" type="checkbox">
      <span>Auto-advance</span>
    </label>
  `);
  const loopInput = $("loopReplayInput");
  loopInput.checked = state.loopEnabled;
  loopInput.addEventListener("change", () => {
    state.loopEnabled = loopInput.checked;
    saveLoopSetting();
    if (state.loopEnabled && state.currentPrompt) {
      replayCurrentPrompt();
    } else if (!state.loopEnabled && state.loopTimer) {
      window.clearTimeout(state.loopTimer);
      state.loopTimer = 0;
    }
  });
  const autoAdvanceInput = $("autoAdvanceInput");
  autoAdvanceInput.checked = state.autoAdvanceEnabled;
  autoAdvanceInput.addEventListener("change", () => {
    state.autoAdvanceEnabled = autoAdvanceInput.checked;
    saveAutoAdvanceSetting();
    if (!state.autoAdvanceEnabled) {
      clearAutoAdvanceTimer();
    } else if (state.selectedInterval?.id === state.currentPrompt?.interval?.id) {
      scheduleAutoAdvance();
    }
  });
}

function setPlaybackControlsVisible(visible) {
  $("playButton")?.classList.toggle("d-none", !visible);
  document.querySelectorAll(".loop-replay-control").forEach((control) => {
    control.classList.toggle("d-none", !visible);
  });
}

function replayCurrentPrompt() {
  if (!state.currentPrompt) {
    return;
  }
  playPrompt(state.currentPrompt).catch((error) => {
    $("statusLine").textContent = error.message || "Could not load that sound.";
  });
}

async function playIntroInterval(semitones, rootMidi = 60) {
  const targetMidi = rootMidi + semitones;
  stopPromptAudio();
  const token = state.promptPlaybackToken;
  highlightPiano(rootMidi, targetMidi);
  try {
    await playMidi(rootMidi, 0.55, 0);
    if (token !== state.promptPlaybackToken) {
      return;
    }
    const timer = window.setTimeout(() => {
      if (token !== state.promptPlaybackToken) {
        return;
      }
      playMidi(targetMidi, 0.72, 0).catch(() => {});
    }, 680);
    state.promptPlaybackTimers.push(timer);
  } catch (error) {
    const status = $("introStatus");
    if (status) {
      status.textContent = error.message || "Could not load that sound.";
    }
  }
}

async function playIntroChord(chordId, rootMidi = 60) {
  const chord = CHORD_BY_ID.get(chordId);
  if (!chord) {
    return;
  }
  rootMidi = chordRootFor(chord, rootMidi);
  const chordMidis = chord.intervals.map((offset) => rootMidi + offset);
  stopPromptAudio();
  highlightPiano(chordMidis, chordMidis[0]);
  try {
    await Promise.all(chordMidis.map((midi) => playMidi(midi, 1.2, 0)));
  } catch (error) {
    const status = $("introStatus");
    if (status) {
      status.textContent = error.message || "Could not load that sound.";
    }
  }
}

function answerByShortcut(key) {
  const button = document.querySelector(`[data-answer-key="${key}"]`);
  if (!button || button.disabled) {
    return false;
  }
  button.click();
  return true;
}

function canAdvancePrompt() {
  return Boolean(state.currentPrompt && state.selectedInterval && state.stage !== "practice-summary" && state.stage !== "test-summary");
}

function eventTargetAcceptsText(event) {
  const target = event.target;
  if (!target || typeof target.matches !== "function") {
    return false;
  }
  if (target.matches("input")) {
    const type = String(target.getAttribute("type") || "text").toLowerCase();
    return !["button", "checkbox", "radio", "range", "submit", "reset"].includes(type);
  }
  return target.isContentEditable
    || target.matches("textarea, select")
    || target.getAttribute("role") === "textbox";
}

function handleTrainerKeydown(event) {
  if (eventTargetAcceptsText(event)) {
    return;
  }

  const key = event.key.toLowerCase();
  if (event.code === "Space") {
    event.preventDefault();
    replayCurrentPrompt();
    return;
  }

  if (NOTE_KEY_MIDI.has(key)) {
    event.preventDefault();
    flashPianoKey(NOTE_KEY_MIDI.get(key));
    playMidi(NOTE_KEY_MIDI.get(key), 0.5).catch(() => {});
    return;
  }

  if (/^[1-8]$/.test(event.key) || ["q", "w", "e", "r", "t", "y"].includes(key)) {
    if (answerByShortcut(key)) {
      event.preventDefault();
    }
    return;
  }

  if (event.key === "Enter" || event.key === "ArrowRight") {
    if (event.key === "Enter" && state.stage === "blind-practice-notice") {
      event.preventDefault();
      $("continueBlindPracticeButton")?.click();
      return;
    }
    if (event.key === "Enter" && state.stage === "ready") {
      event.preventDefault();
      $("startButton")?.click();
      return;
    }
    if (canAdvancePrompt()) {
      event.preventDefault();
      commitSelectedAnswer();
    }
  }
}

function conceptPayload() {
  return Array.from(state.conceptStats.values()).map((stat) => {
    const accuracy = stat.attempts ? (stat.correct / stat.attempts) * 100 : 0;
    return {
      conceptId: stat.conceptId,
      conceptType: stat.conceptType,
      label: stat.label,
      attempts: stat.attempts,
      correct: stat.correct,
      avgResponseMs: Math.round(stat.responseMsTotal / Math.max(1, stat.attempts)),
      confidence: Math.round(stat.confidenceTotal / Math.max(1, stat.attempts)),
      retention: accuracy,
      difficulty: Math.max(0, Math.min(1, 1 - accuracy / 100)),
      stability: Math.max(0.5, 1 + stat.correct - (stat.attempts - stat.correct) * 0.35),
      weakCount: stat.attempts - stat.correct,
    };
  });
}

async function saveLessonAttempt() {
  const score = (state.correct / TEST_TOTAL) * 100;
  const totalAnswered = state.practiceCompleted + state.testCompleted;
  const avgResponseMs = Math.round(state.totalResponseMs / Math.max(1, totalAnswered));
  const confidence = Math.round(state.confidenceTotal / Math.max(1, totalAnswered));
  $("statusLine").textContent = "Saving progress...";
  $("saveTestButton")?.setAttribute("disabled", "disabled");
  try {
    state.lesson = (await fetchJson(`${API_BASE}/modules/${state.moduleId}/lessons/${state.lessonConfig.id}/attempts`, {
      method: "POST",
      body: JSON.stringify({
        mode: "test",
        score,
        accuracy: score,
        avgResponseMs,
        confidence,
        practiceCompleted: state.practiceCompleted,
        testCompleted: state.testCompleted,
        completed: true,
        concepts: conceptPayload(),
        result: {
          schema: "BardsCornerEarTrainingAttempt",
          moduleId: state.moduleId,
          lessonId: state.lessonConfig.id,
          correct: state.correct,
          testTotal: TEST_TOTAL,
          completedAt: new Date().toISOString(),
        },
      }),
    })).lesson;
    $("statusLine").textContent = `Saved. Lesson score: ${scoreText(score)}.`;
    window.location.href = `/ear-training/${state.moduleId}/`;
  } catch (error) {
    $("saveTestButton")?.removeAttribute("disabled");
    $("statusLine").textContent = error.message || "Could not save progress.";
  }
}

async function initLanding() {
  try {
    state.summary = await fetchJson(`${API_BASE}/summary`);
    renderLanding(state.summary);
  } catch {
    renderLanding(null);
    $("landingStatus").innerHTML = `Sign in to track progress. <a href="/login.htm">Login</a>`;
  }
}

async function initSectionOverview() {
  try {
    state.summary = await fetchJson(`${API_BASE}/summary`);
    renderSectionOverview(state.summary);
  } catch {
    renderSectionOverview(null);
    $("singleIntervalsStatus").innerHTML = `Sign in to track progress. <a href="/login.htm">Login</a>`;
  }
}

function initSingleIntervalsIntro() {
  renderPiano($("introPiano"), (midi) => {
    flashPianoKey(midi);
    playMidi(midi, 0.5).catch(() => {});
  });
  document.querySelectorAll("[data-intro-semitones]").forEach((button) => {
    button.addEventListener("click", () => {
      const semitones = Number(button.dataset.introSemitones);
      const rootMidi = button.dataset.introRoot === "random"
        ? 60 + Math.floor(Math.random() * 12)
        : Number(button.dataset.introRoot || 60);
      if (Number.isFinite(semitones)) {
        playIntroInterval(semitones, rootMidi);
      }
    });
  });
  document.querySelectorAll("[data-intro-chord]").forEach((button) => {
    button.addEventListener("click", () => {
      const rootMidi = button.dataset.introRoot === "random"
        ? 60 + Math.floor(Math.random() * 12)
        : Number(button.dataset.introRoot || 60);
      playIntroChord(button.dataset.introChord, rootMidi);
    });
  });
  $("introP4Button")?.addEventListener("click", () => playIntroInterval(5));
  $("introP5Button")?.addEventListener("click", () => playIntroInterval(7));
  $("introFormulaP4Button")?.addEventListener("click", () => playIntroInterval(5));
  $("introFormulaP5Button")?.addEventListener("click", () => playIntroInterval(7));
}

async function initLesson() {
  state.moduleId = sectionIdFromPath();
  state.lessonConfig = sectionLessonConfig();
  loadLoopSetting();
  loadAutoAdvanceSetting();
  renderPiano($("piano"), (midi) => playMidi(midi, 0.5).catch(() => {}));
  renderPlaybackControls();
  renderShortcutHelp();
  renderLessonSkillList();
  $("scoreReadout").textContent = "Practice: 0 correct, 0 incorrect";
  if ($("lessonTitle")) {
    $("lessonTitle").textContent = state.lessonConfig.title;
  }
  try {
    const body = await fetchJson(`${API_BASE}/modules/${state.moduleId}/lessons/${state.lessonConfig.id}`);
    state.lesson = body.lesson;
    state.concepts = body.concepts || [];
    renderLessonSkillList();
    $("lessonBest").textContent = state.lesson ? `${scoreText(state.lesson.bestScore)} ${starString(state.lesson.stars)}` : "0% ☆☆☆";
  } catch {
    $("statusLine").innerHTML = `Sign in to save progress. <a href="/login.htm">Login</a>`;
  }

  $("playButton").addEventListener("click", () => {
    replayCurrentPrompt();
  });
  $("nextButton").addEventListener("click", commitSelectedAnswer);
  $("startButton").addEventListener("click", async () => {
    state.stage = "practice";
    $("startButton").classList.add("d-none");
    setPlaybackControlsVisible(true);
    await nextPrompt();
  });
  document.addEventListener("keydown", handleTrainerKeydown);
}

handleModuleDeepLink();

if ($("moduleGrid")) {
  initLanding();
}

if ($("singleIntervalsOverview")) {
  initSectionOverview();
}

if ($("singleIntervalsIntro") || $("introPiano")) {
  initSingleIntervalsIntro();
}

if ($("trainerPanel")) {
  initLesson();
}
